import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
//components
import { Events } from "@uaveiro/ui";
//Hocs
import { compose } from "../../services/utilities";
//Utils
import moment from "moment";
import { portalAPI } from "../../services/constants";
//Locales
import { injectIntl } from "react-intl";
//Hooks
import { useQueryBuilder } from "../../hooks";

const EventsTemplate = ({ match, endpoint, history, ...props }) => {
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [isAccessibleFocus, setAccessibleFocus] = useState(false);
  const [hasError, setError] = useState(false);
  const [content, setContent] = useState({});
  const [range, setRange] = useState([]);
  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [recordsPerPage, setRecordsPerPage] = useState(4);

  const [currentChannel, setCurrentChannel] = useState(57);
  const [currentCategory, setCurrentCategory] = useState(-1);

  const [
    urlParams,
    setUrlParams,
    searchParams,
    remoteUrlParam
  ] = useQueryBuilder(match.url, history.location.search);

  const { lang } = match.params;

  const openCalendar = () => {
    setOpen(!isOpen);
  };

  const onFetchData = async (
    date,
    category = -1,
    channel = 57,
    recordsPerPage = 4
  ) => {
    try {
      const res = await fetch(
        `${portalAPI}${endpoint}?channel=${channel}&categorie=${category}&language=${lang}&recordsPerPage=${recordsPerPage}&startDate=${moment(
          date
        ).format("DD-MM-YYYY")}`
      );
      const content = await res.json();

      setContent({ ...content.data });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(true);
    }
  };

  const onFetchCategories = async () => {
    try {
      const res = await fetch(`${portalAPI}/news/categories?language=${lang}`);
      const content = await res.json();

      const options = content.data.map(item => ({
        value: item.id,
        label: item.name
      }));

      setCategories(options);
    } catch (error) {
      setError(true);
    }
  };

  const onFetchChannels = async () => {
    try {
      const res = await fetch(`${portalAPI}/events/channel?language=${lang}`);
      const content = await res.json();

      const options = content.data.map(item => ({
        value: item.id,
        label: item.name
      }));
      setChannels(options);
    } catch (error) {
      setError(true);
    }
  };

  useEffect(() => {
    onFetchChannels();
    onFetchCategories();
    onFetchData(
      currentDate,
      searchParams.category,
      searchParams.channel,
      searchParams.recordsPerPage
    );
  }, [history.location]);

  const onCreateRange = (date = new Date()) => {
    const fromDate = moment(date);
    const toDate = moment(date).add(7, "days");

    const range = moment.range(fromDate, toDate);
    const days = Array.from(range.by("day"));

    setRange(
      days.map(m => {
        const day =
          lang === "pt" ? m.format("dddd").split("-")[0] : m.format("dddd");
        return {
          m,
          day: `${day}, ${m.format("DD")}`,
          month: m.format("MMM"),
          monthExtended: m.format("MMMM")
        };
      })
    );
  };

  useEffect(() => {
    onCreateRange();
  }, []);

  const onChangeDate = (date, fromCalendar = true) => {
    const current = fromCalendar ? date : date.toDate();
    setLoading(true);
    setAccessibleFocus(true);
    onFetchData(current, currentCategory, currentChannel);
    setCurrentDate(current);
    onCreateRange(date);
  };

  const onResetDate = () => {
    if (!moment(currentDate).isSame(new Date(), "day")) {
      setCurrentDate(new Date());
      setLoading(true);
      onFetchData(new Date(), currentCategory, currentChannel);
      onCreateRange();
      return;
    }
  };

  const onChangeCategory = option => {
    setLoading(true);
    if (option !== null) {
      setUrlParams({
        ...urlParams,
        category: option.value
      });
      setCurrentCategory(option.value);
      onFetchData(currentDate, option.value, currentChannel);
      return;
    }
    //Removes "category" param from url
    remoteUrlParam("category");
    setCurrentCategory(-1);
    onFetchData(currentDate, -1, currentChannel);
  };

  const onChangeChannel = option => {
    setLoading(true);
    if (option !== null) {
      setUrlParams({
        ...urlParams,
        channel: option.value
      });
      setCurrentChannel(option.value);
      onFetchData(currentDate, currentCategory, option.value);
      return;
    }
    //Removes "channel" param from url
    remoteUrlParam("channel");
    setCurrentChannel(57);
    onFetchData(currentDate, currentCategory, 57);
  };

  const onLoadMore = () => {
    onFetchData(
      currentDate,
      currentCategory,
      currentChannel,
      recordsPerPage + 4
    );
    setRecordsPerPage(prevState => prevState + 4);
  };

  const EventProps = {
    isLoading,
    content,
    hasError,
    range,
    categories,
    channels,
    currentDate,
    openCalendar,
    onChangeDate,
    onResetDate,
    onChangeCategory,
    onChangeChannel,
    onLoadMore,
    setOpen,
    isOpen,
    isAccessibleFocus,
    ...props
  };

  return <Events {...EventProps} />;
};

EventsTemplate.propTypes = {
  endpoint: PropTypes.string.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
};

export default compose(withRouter, injectIntl)(EventsTemplate);
